import { useEffect, useState } from "react";
import type { RevisionItem } from "@yushi-portfolio/api-types";
import type { ApiClient } from "../lib/api";
import { describeError } from "../lib/editor";
import { usePublishing } from "../lib/publishing";

/** Publish button with confirmation, errors and the build hand-off message. */
export function PublishButton(props: {
  disabledReason?: string;
  publish: () => Promise<unknown>;
  onPublished: () => void;
}) {
  const { refresh } = usePublishing();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string; details?: string[] }>();

  async function run() {
    if (!window.confirm("この内容を公開サイトに反映します。よろしいですか？")) return;
    setBusy(true);
    setMessage(undefined);
    try {
      await props.publish();
      setMessage({ kind: "ok", text: "公開しました。サイトへの反映には数分かかります。" });
      props.onPublished();
      void refresh();
    } catch (e) {
      const err = describeError(e);
      setMessage({ kind: "error", text: err.message, details: err.details });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="publish" disabled={busy || Boolean(props.disabledReason)} title={props.disabledReason} onClick={run}>
        {busy ? "公開中…" : "Publish"}
      </button>
      {message && (
        <div className={`notice ${message.kind} floating`} role="status">
          <strong>{message.text}</strong>
          {message.details && message.details.length > 0 && (
            <ul>
              {message.details.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

export function RevisionList({ api, kind, id, reloadKey }: { api: ApiClient; kind: "works" | "exhibitions"; id: string; reloadKey: number }) {
  const [items, setItems] = useState<RevisionItem[]>();
  useEffect(() => {
    let active = true;
    api.revisions(kind, id).then(
      (r) => active && setItems(r.items),
      () => active && setItems([]),
    );
    return () => {
      active = false;
    };
  }, [kind, id, reloadKey]);
  if (!items) return <p className="muted">読み込み中…</p>;
  if (items.length === 0) return <p className="muted">まだ公開されていません。</p>;
  return (
    <table className="list">
      <thead>
        <tr>
          <th>公開日時</th>
          <th>version</th>
        </tr>
      </thead>
      <tbody>
        {items.map((r) => (
          <tr key={r.publishedAt}>
            <td>{new Date(r.publishedAt).toLocaleString("ja-JP")}</td>
            <td>{r.version ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
